import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPendingDisbursementAdvances,
  assignAdvanceDisbursement,
  postponeAdvanceDisbursement,
  returnAdvanceToHr,
} from "../../api/advances";
import { mapAdvanceRow } from "../../utils/dataMappers";
import {
  addDaysToDateKey,
  isAdvanceDisbursementPostponed,
  localDateKey,
} from "../../utils/advanceDisbursement";
import AdvancePostponeModal from "./AdvancePostponeModal";
import "./AdvanceDisbursementRouting.css";

function formatAmount(amount) {
  const value = Number(amount);
  if (Number.isNaN(value)) return "—";
  return `${value.toLocaleString("ar-EG")} ج.م`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("ar-EG");
}

function advanceTypeLabel(row) {
  return Number(row.installments) > 1 ? "سلفة مقسطة" : "سلفة عادية";
}

function assigneeOptionLabel(target) {
  const parts = [target.fullName];
  if (target.employeeCode) parts.push(`(${target.employeeCode})`);
  if (target.jobTitle) parts.push(`— ${target.jobTitle}`);
  if (target.branchName) parts.push(`— ${target.branchName}`);
  return parts.join(" ");
}

function isBranchCashierRouting(row) {
  return (
    row.disbursementTargetType === "CASHIER" || row.assignToAllBranchCashiers
  );
}

function branchCashierRoutingLabel(row, targets) {
  const branch = row.branchName || "الفرع";
  const count = targets.length;
  if (count > 0) {
    return `كاشيرية فرع ${branch} (${count} كاشير)`;
  }
  return `كاشيرية فرع ${branch}`;
}

function emptyTargetsHint(row) {
  return isBranchCashierRouting(row)
    ? "لا يوجد كاشير نشط في فرع الموظف"
    : "لا يوجد مسؤول خزنة نشط";
}

const NO_BRANCH = "__no_branch__";
const NO_DEPARTMENT = "__no_department__";

function uniqueSorted(values) {
  return [...new Set(values.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, "ar"),
  );
}

function rowBranchKey(row) {
  return row.branchName || NO_BRANCH;
}

function rowDepartmentKey(row) {
  return row.departmentName || NO_DEPARTMENT;
}

export default function AdvanceDisbursementRouting() {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [selectedById, setSelectedById] = useState({});
  const [selectedBranchKeys, setSelectedBranchKeys] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [bulkMessage, setBulkMessage] = useState("");
  const [submittingId, setSubmittingId] = useState("");
  const [submittingAction, setSubmittingAction] = useState("");
  const [postponeModal, setPostponeModal] = useState({
    open: false,
    row: null,
    date: "",
    reason: "",
    error: "",
  });

  const loadAdvances = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    setLoadError("");
    try {
      const rows = await getPendingDisbursementAdvances();
      const list = (Array.isArray(rows) ? rows : rows?.items || []).map(
        mapAdvanceRow,
      );
      setAdvances(list);
      setSelectedById((prev) => {
        const next = {};
        for (const row of list) {
          if (isBranchCashierRouting(row)) {
            continue;
          }
          const targets = row.disbursementTargets || [];
          const previous = prev[row.id];
          const stillValid = targets.some((t) => t.employeeId === previous);
          if (stillValid) {
            next[row.id] = previous;
          } else if (targets.length === 1) {
            next[row.id] = targets[0].employeeId;
          } else {
            next[row.id] = "";
          }
        }
        return next;
      });
      setSelectedBranchKeys((prev) => {
        const validKeys = new Set(
          list
            .filter((row) => !isAdvanceDisbursementPostponed(row))
            .map(rowBranchKey),
        );
        const next = {};
        for (const key of Object.keys(prev)) {
          if (prev[key] && validKeys.has(key)) next[key] = true;
        }
        return next;
      });
      setRowErrors({});
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          err?.message ||
          "تعذر تحميل السلف بانتظار توجيه الصرف",
      );
      setAdvances([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdvances();
  }, [loadAdvances]);

  const branchOptions = useMemo(() => {
    const names = uniqueSorted(advances.map((row) => row.branchName));
    const hasNoBranch = advances.some((row) => !row.branchName);
    return hasNoBranch ? [...names, NO_BRANCH] : names;
  }, [advances]);

  const departmentOptions = useMemo(() => {
    const scoped = branchFilter
      ? advances.filter((row) => rowBranchKey(row) === branchFilter)
      : advances;
    const names = uniqueSorted(scoped.map((row) => row.departmentName));
    const hasNoDepartment = scoped.some((row) => !row.departmentName);
    return hasNoDepartment ? [...names, NO_DEPARTMENT] : names;
  }, [advances, branchFilter]);

  useEffect(() => {
    if (departmentFilter && !departmentOptions.includes(departmentFilter)) {
      setDepartmentFilter("");
    }
  }, [departmentFilter, departmentOptions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return advances.filter((row) => {
      if (branchFilter && rowBranchKey(row) !== branchFilter) return false;
      if (departmentFilter && rowDepartmentKey(row) !== departmentFilter) {
        return false;
      }
      if (!q) return true;
      const haystack = [
        row.employeeName,
        row.employeeCode,
        row.departmentName,
        row.branchName,
        row.reason,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [advances, search, branchFilter, departmentFilter]);

  const allDueAdvances = useMemo(
    () => advances.filter((row) => !isAdvanceDisbursementPostponed(row)),
    [advances],
  );

  const dueAdvances = useMemo(
    () => filtered.filter((row) => !isAdvanceDisbursementPostponed(row)),
    [filtered],
  );

  const postponedAdvances = useMemo(
    () => filtered.filter((row) => isAdvanceDisbursementPostponed(row)),
    [filtered],
  );

  const totalAmount = useMemo(
    () =>
      dueAdvances.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [dueAdvances],
  );

  const branchBulkOptions = useMemo(() => {
    const byKey = new Map();
    for (const row of allDueAdvances) {
      const key = rowBranchKey(row);
      const current = byKey.get(key) || {
        key,
        label: key === NO_BRANCH ? "بدون فرع (إدارة)" : key,
        count: 0,
        assignableCount: 0,
        amount: 0,
      };
      current.count += 1;
      current.amount += Number(row.amount) || 0;
      const targets = row.disbursementTargets || [];
      const branchCashiers = isBranchCashierRouting(row);
      const canAssign =
        targets.length > 0 &&
        (branchCashiers || Boolean(selectedById[row.id]));
      if (canAssign) current.assignableCount += 1;
      byKey.set(key, current);
    }
    return [...byKey.values()].sort((a, b) =>
      a.label.localeCompare(b.label, "ar"),
    );
  }, [allDueAdvances, selectedById]);

  const selectedBranchList = useMemo(
    () => branchBulkOptions.filter((b) => selectedBranchKeys[b.key]),
    [branchBulkOptions, selectedBranchKeys],
  );

  const bulkAssignableRows = useMemo(() => {
    const selected = new Set(
      Object.keys(selectedBranchKeys).filter((key) => selectedBranchKeys[key]),
    );
    if (selected.size === 0) return [];
    return allDueAdvances.filter((row) => {
      if (!selected.has(rowBranchKey(row))) return false;
      const targets = row.disbursementTargets || [];
      if (targets.length === 0) return false;
      if (isBranchCashierRouting(row)) return true;
      return Boolean(selectedById[row.id]);
    });
  }, [allDueAdvances, selectedBranchKeys, selectedById]);

  const bulkSkippedCount = useMemo(() => {
    const selected = new Set(
      Object.keys(selectedBranchKeys).filter((key) => selectedBranchKeys[key]),
    );
    if (selected.size === 0) return 0;
    const assignableIds = new Set(bulkAssignableRows.map((row) => row.id));
    return allDueAdvances.filter(
      (row) =>
        selected.has(rowBranchKey(row)) && !assignableIds.has(row.id),
    ).length;
  }, [allDueAdvances, selectedBranchKeys, bulkAssignableRows]);

  const toggleBranchKey = (key) => {
    setSelectedBranchKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setBulkMessage("");
  };

  const selectAllBranches = () => {
    const next = {};
    for (const option of branchBulkOptions) {
      if (option.assignableCount > 0) next[option.key] = true;
    }
    setSelectedBranchKeys(next);
    setBulkMessage("");
  };

  const clearSelectedBranches = () => {
    setSelectedBranchKeys({});
    setBulkMessage("");
  };

  const submitBulkAssign = async () => {
    if (bulkAssignableRows.length === 0 || submittingId) return;

    const branchLabels = selectedBranchList.map((b) => b.label).join("، ");
    const skipNote =
      bulkSkippedCount > 0
        ? `\nملاحظة: ${bulkSkippedCount} سلفة لن تُوجَّه (لا يوجد مستلم أو لم يُختر مسؤول خزنة).`
        : "";
    const ok = window.confirm(
      `توجيه ${bulkAssignableRows.length} سلفة للفروع المحددة (${branchLabels})؟${skipNote}`,
    );
    if (!ok) return;

    setSubmittingId("__bulk__");
    setSubmittingAction("bulk-assign");
    setBulkMessage("");
    setRowErrors({});

    let successCount = 0;
    const failed = [];

    for (const row of bulkAssignableRows) {
      const branchCashiers = isBranchCashierRouting(row);
      const assigneeEmployeeId = branchCashiers
        ? undefined
        : selectedById[row.id];
      try {
        await assignAdvanceDisbursement(
          row.id,
          assigneeEmployeeId ? { assigneeEmployeeId } : {},
        );
        successCount += 1;
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "تعذر توجيه الصرف";
        failed.push({ id: row.id, message });
      }
    }

    if (failed.length > 0) {
      setRowErrors((prev) => {
        const next = { ...prev };
        for (const item of failed) {
          next[item.id] = item.message;
        }
        return next;
      });
    }

    if (successCount > 0 && failed.length === 0) {
      setBulkMessage(
        `تم توجيه ${successCount} سلفة بنجاح للفروع المحددة.`,
      );
      setSelectedBranchKeys({});
    } else if (successCount > 0) {
      setBulkMessage(
        `تم توجيه ${successCount} سلفة، وتعذر توجيه ${failed.length} سلفة.`,
      );
    } else {
      setBulkMessage(
        failed[0]?.message || "تعذر توجيه سلف الفروع المحددة",
      );
    }

    await loadAdvances({ silent: true });
    setSubmittingId("");
    setSubmittingAction("");
  };

  const submitAssign = async (row) => {
    const branchCashiers = isBranchCashierRouting(row);
    const assigneeEmployeeId = branchCashiers ? undefined : selectedById[row.id];

    if (!branchCashiers && !assigneeEmployeeId) {
      setRowErrors((prev) => ({
        ...prev,
        [row.id]: "اختر مسؤول الخزنة الذي سيصرف السلفة",
      }));
      return;
    }

    setSubmittingId(row.id);
    setSubmittingAction("assign");
    setBulkMessage("");
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });

    try {
      await assignAdvanceDisbursement(
        row.id,
        assigneeEmployeeId ? { assigneeEmployeeId } : {},
      );
      await loadAdvances({ silent: true });
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [row.id]:
          err?.response?.data?.message ||
          err?.message ||
          "تعذر توجيه الصرف",
      }));
    } finally {
      setSubmittingId("");
      setSubmittingAction("");
    }
  };

  const openPostponeModal = (row) => {
    if (!row?.id || submittingId) return;
    setPostponeModal({
      open: true,
      row,
      date: addDaysToDateKey(localDateKey(), 1),
      reason: row.disbursementPostponeReason || "",
      error: "",
    });
  };

  const closePostponeModal = () => {
    if (submittingAction === "postpone") return;
    setPostponeModal({
      open: false,
      row: null,
      date: "",
      reason: "",
      error: "",
    });
  };

  const submitPostpone = async () => {
    const row = postponeModal.row;
    if (!row?.id || submittingId) return;

    const date = String(postponeModal.date || "").trim();
    const reason = String(postponeModal.reason || "").trim();
    const minDate = addDaysToDateKey(localDateKey(), 1);

    if (!date || date < minDate) {
      setPostponeModal((prev) => ({
        ...prev,
        error: "يجب اختيار يوم لاحق لتأجيل الصرف",
      }));
      return;
    }
    if (reason.length < 3) {
      setPostponeModal((prev) => ({
        ...prev,
        error: "سبب التأجيل إجباري",
      }));
      return;
    }

    setSubmittingId(row.id);
    setSubmittingAction("postpone");
    setPostponeModal((prev) => ({ ...prev, error: "" }));
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });

    try {
      await postponeAdvanceDisbursement(row.id, {
        postponedUntil: date,
        notes: reason,
      });
      setPostponeModal({
        open: false,
        row: null,
        date: "",
        reason: "",
        error: "",
      });
      await loadAdvances({ silent: true });
    } catch (err) {
      setPostponeModal((prev) => ({
        ...prev,
        error: Array.isArray(err?.response?.data?.message)
          ? err.response.data.message.join("\n")
          : err?.response?.data?.message ||
            err?.message ||
            "تعذر تأجيل صرف السلفة",
      }));
    } finally {
      setSubmittingId("");
      setSubmittingAction("");
    }
  };

  const submitReturnToHr = async (row) => {
    if (!row?.id || submittingId) return;
    const ok = window.confirm(
      `إرجاع سلفة ${row.employeeName || "الموظف"} بمبلغ ${formatAmount(row.amount)} للموارد البشرية؟ لن تُصرف، ويمكن للموارد البشرية تعديلها أو إلغاؤها.`,
    );
    if (!ok) return;

    setSubmittingId(row.id);
    setSubmittingAction("return-hr");
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });

    try {
      await returnAdvanceToHr(row.id);
      await loadAdvances({ silent: true });
    } catch (err) {
      setRowErrors((prev) => ({
        ...prev,
        [row.id]:
          err?.response?.data?.message ||
          err?.message ||
          "تعذر إرجاع السلفة للموارد البشرية",
      }));
    } finally {
      setSubmittingId("");
      setSubmittingAction("");
    }
  };

  return (
    <div className="adv-disburse is-wide" dir="rtl">
      <div className="adv-disburse-header">
        <div>
          <h2 className="adv-disburse-title">توجيه صرف السلف</h2>
          <p className="adv-disburse-subtitle">
            السلفة العادية داخل الفرع تُوجَّه تلقائياً لكل كاشيرية نفس الفرع،
            وأي كاشير يمكنه تأكيد الصرف. السلفة المقسطة أو إدارة الحسابات
            تُوجَّه لمسؤول الخزنة. لا يمكن إلغاء السلفة من هنا — يمكن تأجيل
            الصرف، أو إرجاعها للموارد البشرية لتعديلها أو إلغائها.
          </p>
        </div>
        <button
          type="button"
          className="adv-disburse-refresh"
          onClick={loadAdvances}
          disabled={loading || Boolean(submittingId)}
        >
          تحديث
        </button>
      </div>

      <div className="adv-disburse-toolbar">
        <input
          type="search"
          className="adv-disburse-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="بحث بالاسم أو الكود..."
          disabled={loading}
        />
        <select
          className="adv-disburse-filter-select"
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.target.value)}
          disabled={loading || advances.length === 0}
          aria-label="فلتر الفرع"
        >
          <option value="">كل الفروع</option>
          {branchOptions.map((name) => (
            <option key={name} value={name}>
              {name === NO_BRANCH ? "بدون فرع (إدارة)" : name}
            </option>
          ))}
        </select>
        <select
          className="adv-disburse-filter-select"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          disabled={loading || advances.length === 0}
          aria-label="فلتر القسم"
        >
          <option value="">كل الأقسام</option>
          {departmentOptions.map((name) => (
            <option key={name} value={name}>
              {name === NO_DEPARTMENT ? "بدون قسم" : name}
            </option>
          ))}
        </select>
        <span className="adv-disburse-count">
          {filtered.length} من {advances.length}
        </span>
      </div>

      {!loading && !loadError && branchBulkOptions.length > 0 ? (
        <div className="adv-disburse-bulk-panel">
          <div className="adv-disburse-bulk-header">
            <div>
              <h3 className="adv-disburse-bulk-title">
                توجيه جماعي حسب الفرع
              </h3>
              <p className="adv-disburse-bulk-hint">
                اختر فرعاً واحداً أو أكثر ثم وجّه كل سلفها دفعة واحدة. سلف الفرع
                العادية تذهب لكاشيرية الفرع، والسلف المقسطة تحتاج اختيار مسؤول
                خزنة في الجدول أولاً.
              </p>
            </div>
            <div className="adv-disburse-bulk-actions">
              <button
                type="button"
                className="adv-disburse-btn ghost compact"
                onClick={selectAllBranches}
                disabled={
                  Boolean(submittingId) ||
                  branchBulkOptions.every((b) => b.assignableCount === 0)
                }
              >
                تحديد الكل
              </button>
              <button
                type="button"
                className="adv-disburse-btn ghost compact"
                onClick={clearSelectedBranches}
                disabled={
                  Boolean(submittingId) || selectedBranchList.length === 0
                }
              >
                مسح التحديد
              </button>
              <button
                type="button"
                className="adv-disburse-btn compact"
                onClick={submitBulkAssign}
                disabled={
                  Boolean(submittingId) || bulkAssignableRows.length === 0
                }
              >
                {submittingAction === "bulk-assign"
                  ? "جاري التوجيه الجماعي..."
                  : `توجيه سلف الفروع المحددة (${bulkAssignableRows.length})`}
              </button>
            </div>
          </div>
          <div className="adv-disburse-branch-checks">
            {branchBulkOptions.map((option) => {
              const checked = Boolean(selectedBranchKeys[option.key]);
              const disabled =
                Boolean(submittingId) || option.assignableCount === 0;
              return (
                <label
                  key={option.key}
                  className={`adv-disburse-branch-check ${
                    checked ? "is-checked" : ""
                  } ${disabled ? "is-disabled" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleBranchKey(option.key)}
                  />
                  <span className="adv-disburse-branch-check-body">
                    <strong>{option.label}</strong>
                    <span>
                      {option.assignableCount} من {option.count} جاهزة —{" "}
                      {formatAmount(option.amount)}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          {bulkMessage ? (
            <div
              className={`adv-disburse-bulk-message ${
                bulkMessage.includes("تعذر") &&
                !bulkMessage.includes("تم توجيه")
                  ? "is-error"
                  : "is-ok"
              }`}
            >
              {bulkMessage}
            </div>
          ) : null}
        </div>
      ) : null}

      {loadError ? (
        <div className="adv-disburse-error-banner">{loadError}</div>
      ) : loading ? (
        <div className="adv-disburse-empty">جاري التحميل...</div>
      ) : filtered.length === 0 ? (
        <div className="adv-disburse-empty">
          {advances.length === 0
            ? "لا توجد سلف بانتظار توجيه الصرف حالياً"
            : "لا توجد نتائج مطابقة للبحث أو الفلاتر"}
        </div>
      ) : (
        <>
          {dueAdvances.length > 0 ? (
            <div className="adv-disburse-table-wrap">
              <table className="adv-disburse-table">
                <thead>
                  <tr>
                    <th>الموظف</th>
                    <th>الكود</th>
                    <th>الفرع</th>
                    <th>القسم</th>
                    <th>المبلغ</th>
                    <th>النوع</th>
                    <th>تاريخ الاعتماد</th>
                    <th>توجيه الصرف إلى</th>
                    <th>إجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {dueAdvances.map((row) => {
                    const targets = row.disbursementTargets || [];
                    const branchCashiers = isBranchCashierRouting(row);
                    const selected = selectedById[row.id] || "";
                    const busy = submittingId === row.id;
                    const error = rowErrors[row.id];
                    const canAssign = targets.length > 0;
                    const assignDisabled =
                      Boolean(submittingId) ||
                      !canAssign ||
                      (!branchCashiers && !selected);

                    return (
                      <tr key={row.id}>
                        <td className="is-name">
                          <div className="adv-disburse-name-cell">
                            <strong>{row.employeeName || "—"}</strong>
                            {row.reasonText || row.reason ? (
                              <span className="adv-disburse-reason-hint">
                                {row.reasonText || row.reason}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td>{row.employeeCode || "—"}</td>
                        <td>{row.branchName || "—"}</td>
                        <td>{row.departmentName || "—"}</td>
                        <td>{formatAmount(row.amount)}</td>
                        <td>
                          <span className="adv-disburse-kind">
                            {advanceTypeLabel(row)}
                          </span>
                        </td>
                        <td>{formatDate(row.approvedAt || row.submittedAt)}</td>
                        <td className="is-assign">
                          {!canAssign ? (
                            <span className="adv-disburse-empty-targets">
                              {emptyTargetsHint(row)}
                            </span>
                          ) : branchCashiers ? (
                            <span className="adv-disburse-branch-target">
                              {branchCashierRoutingLabel(row, targets)}
                            </span>
                          ) : (
                            <select
                              className="adv-disburse-inline-select"
                              value={selected}
                              onChange={(e) =>
                                setSelectedById((prev) => ({
                                  ...prev,
                                  [row.id]: e.target.value,
                                }))
                              }
                              disabled={busy}
                            >
                              <option value="">— اختر مسؤول الخزنة —</option>
                              {targets.map((target) => (
                                <option
                                  key={target.employeeId}
                                  value={target.employeeId}
                                >
                                  {assigneeOptionLabel(target)}
                                </option>
                              ))}
                            </select>
                          )}
                          {error ? (
                            <div className="adv-disburse-row-error">{error}</div>
                          ) : null}
                        </td>
                        <td>
                          <div className="adv-row-actions">
                            <button
                              type="button"
                              className="adv-disburse-btn compact"
                              onClick={() => submitAssign(row)}
                              disabled={assignDisabled}
                            >
                              {busy && submittingAction === "assign"
                                ? "جاري التوجيه..."
                                : branchCashiers
                                  ? "توجيه للفرع"
                                  : "توجيه"}
                            </button>
                            <button
                              type="button"
                              className="adv-disburse-btn compact warn"
                              onClick={() => openPostponeModal(row)}
                              disabled={Boolean(submittingId)}
                            >
                              تأجيل
                            </button>
                            <button
                              type="button"
                              className="adv-disburse-btn compact danger"
                              onClick={() => submitReturnToHr(row)}
                              disabled={Boolean(submittingId)}
                            >
                              {busy && submittingAction === "return-hr"
                                ? "جاري الإرجاع..."
                                : "إرجاع للموارد البشرية"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} className="is-total-label">
                      الإجمالي ({dueAdvances.length} سلفة)
                    </td>
                    <td className="is-total-amount">
                      {formatAmount(totalAmount)}
                    </td>
                    <td colSpan={4} />
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="adv-disburse-empty">
              لا توجد سلف جاهزة للتوجيه اليوم
            </div>
          )}

          {postponedAdvances.length > 0 ? (
            <div>
              <h3 className="adv-disburse-section-title">
                سلف مؤجلة الصرف ({postponedAdvances.length})
              </h3>
              <div className="adv-disburse-table-wrap">
                <table className="adv-disburse-table">
                  <thead>
                    <tr>
                      <th>الموظف</th>
                      <th>الكود</th>
                      <th>الفرع</th>
                      <th>القسم</th>
                      <th>المبلغ</th>
                      <th>مؤجلة حتى</th>
                      <th>سبب التأجيل</th>
                      <th>إجراء</th>
                    </tr>
                  </thead>
                  <tbody>
                    {postponedAdvances.map((row) => (
                      <tr key={row.id}>
                        <td className="is-name">
                          <strong>{row.employeeName || "—"}</strong>
                        </td>
                        <td>{row.employeeCode || "—"}</td>
                        <td>{row.branchName || "—"}</td>
                        <td>{row.departmentName || "—"}</td>
                        <td>{formatAmount(row.amount)}</td>
                        <td>{formatDate(row.disbursementPostponedUntil)}</td>
                        <td>
                          <span className="adv-postpone-hint">
                            {row.disbursementPostponeReason || "—"}
                          </span>
                        </td>
                        <td>
                          <div className="adv-row-actions">
                            <button
                              type="button"
                              className="adv-disburse-btn compact warn"
                              onClick={() => openPostponeModal(row)}
                              disabled={Boolean(submittingId)}
                            >
                              تعديل التأجيل
                            </button>
                            <button
                              type="button"
                              className="adv-disburse-btn compact danger"
                              onClick={() => submitReturnToHr(row)}
                              disabled={Boolean(submittingId)}
                            >
                              {submittingId === row.id &&
                              submittingAction === "return-hr"
                                ? "جاري الإرجاع..."
                                : "إرجاع للموارد البشرية"}
                            </button>
                          </div>
                          {rowErrors[row.id] ? (
                            <div className="adv-disburse-row-error">
                              {rowErrors[row.id]}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}

      <AdvancePostponeModal
        open={postponeModal.open}
        row={postponeModal.row}
        date={postponeModal.date}
        reason={postponeModal.reason}
        error={postponeModal.error}
        submitting={submittingAction === "postpone"}
        minDate={addDaysToDateKey(localDateKey(), 1)}
        amountLabel={
          postponeModal.row ? formatAmount(postponeModal.row.amount) : ""
        }
        onDateChange={(value) =>
          setPostponeModal((prev) => ({ ...prev, date: value, error: "" }))
        }
        onReasonChange={(value) =>
          setPostponeModal((prev) => ({ ...prev, reason: value, error: "" }))
        }
        onClose={closePostponeModal}
        onSubmit={submitPostpone}
      />
    </div>
  );
}
